import React, { useRef, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Button } from 'src/components/ui/Button';
import { UploadSorrell, UploadSvg } from 'src/assets/images/svg/Upload';
import Span from 'src/components/ui/Span';
import Preloader from 'src/components/ui/Preloader';
import CloseSvg from 'src/assets/images/svg/CloseSvg';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getFormLabel, getFormInstructions } from 'src/helpers/multilingualHelper';
import { MAX_FILE_SIZE_MB_FP } from 'src/constants/fileConfig';

const isDataUrl = (v) => typeof v === 'string' && v.startsWith('data:');
const isHttpUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);

const guessFileName = (v, t) => {
    if (!v) return null;
    if (isHttpUrl(v)) {
        try {
            const last = v.split('/').pop() || '';
            const clean = last.split('?')[0];
            return decodeURIComponent(clean || t('form.fileUpload.downloadedFile'));
        } catch {
            return t('form.fileUpload.downloadedFile');
        }
    }
    if (isDataUrl(v)) return t('form.fileUpload.uploadedFile');
    return t('form.fileUpload.file');
};

const FileUploadClientForm = ({
    elementId,
    label,
    name,
    required,
    control,
    errors,
    onChange: onChangeCustom,
    value,
    config,
}) => {
    const hiddenFileInput = useRef(null);
    const fileButtonRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState(null);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language || 'en';

    const displayLabel = getFormLabel(label, currentLanguage);
    const displayInstructions = getFormInstructions(config?.instructions, currentLanguage);

    useEffect(() => {
        if (errors[elementId] && fileButtonRef.current) {
            fileButtonRef.current.focus();
        }
    }, [errors, elementId]);

    useEffect(() => {
        if (value) {
            setUploadedFileName(guessFileName(value, t));
        } else {
            setUploadedFileName(null);
        }
    }, [value, t]);

    const handleFileClick = () => hiddenFileInput.current?.click();

    const handleFileChange = (file, onChange) => {
        if (!file) return;

        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > MAX_FILE_SIZE_MB_FP) {
            toast.warning(t('form.fileSizeExceeds', { size: MAX_FILE_SIZE_MB_FP }));
            if (hiddenFileInput.current) hiddenFileInput.current.value = null;
            return;
        }

        setLoading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result;
            onChange(base64String);
            onChangeCustom && onChangeCustom(elementId, null, base64String);
            setUploadedFileName(file.name || t('form.fileUpload.uploadedFile'));
            setLoading(false);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveFile = (onChange) => {
        setUploadedFileName(null);
        onChange(null);
        onChangeCustom && onChangeCustom(elementId, null, null);
        if (hiddenFileInput.current) hiddenFileInput.current.value = null;
    };

    const triggerDownloadUrl = async (url) => {
        try {
            setDownloading(true);

            const res = await fetch(url, { credentials: 'include' });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = guessFileName(url, t) || t('form.fileUpload.file');
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('Download failed:', err);
            window.open(url, '_blank', 'noopener');
        } finally {
            setDownloading(false);
        }
    };

    const triggerDownloadDataUrl = (dataUrl) => {
        try {
            setDownloading(true);
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = guessFileName(dataUrl, t) || t('form.fileUpload.file');
            document.body.appendChild(a);
            a.click();
            a.remove();
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="w-full">
            {displayLabel && (
                <label htmlFor={elementId} className="block mb-2 text-sm  text-gray-700">
                    {displayLabel}{required && '*'}
                </label>
            )}
            {
                config?.hasInstructions && displayInstructions && (
                    <label className="mb-2 text-xs  text-gray-300">
                        {displayInstructions}
                    </label>
                )
            }

            <Controller
                name={name || elementId}
                control={control}
                rules={{ required }}
                render={({ field }) => {
                    if (!field.value && value) {
                        field.onChange(value);
                        if (!uploadedFileName) setUploadedFileName(guessFileName(value, t));
                    }

                    const pickEffective = (fv, pv) => {
                        if (isHttpUrl(pv)) return pv;
                        if (isHttpUrl(fv)) return fv;
                        return fv || pv || null;
                    };

                    const effectiveValue = pickEffective(field.value, value);
                    const hasValue = !!effectiveValue;
                    const isUrl = isHttpUrl(effectiveValue);
                    const isBase64 = isDataUrl(effectiveValue);


                    return (
                        <>
                            <div className="flex flex-col justify-center items-center bg-gray-50 rounded-2xl w-full gap-4 p-4">
                                <UploadSvg />
                                {!hasValue && <span>{t('form.fileUpload.pleaseSelectFile')}</span>}
                                {!hasValue && (
                                    <small className="text-gray-400">
                                        {t('form.fileUpload.fileSizeLimit', { size: MAX_FILE_SIZE_MB_FP })}
                                    </small>
                                )}

                                <div className="group w-full">
                                    {!hasValue && (
                                        <Button
                                            ref={fileButtonRef}
                                            variant=""
                                            htmlType="button"
                                            className={`flex justify-center items-center w-full h-13 rounded-2xl group-hover:bg-SorrellBrown4000 group-hover:text-Gray50 px-4 py-2 border bg-gray-50 text-gray-700 focus:outline-none focus:border-SorrellBrown4000 ${errors[elementId] ? 'border-FailRed400' : 'border-Gray300'
                                                }`}
                                            onClick={handleFileClick}
                                            disabled={loading || downloading}
                                        >
                                            {(loading || downloading) ? <Preloader /> : <UploadSorrell />}
                                            {!loading && !downloading && (
                                                <span className="ms-2 text-SorrellBrown4000 group-hover:text-Gray50 leading-5">
                                                    {t('form.fileUpload.selectFile')}
                                                </span>
                                            )}
                                        </Button>
                                    )}

                                    {hasValue && isUrl && (
                                        <Button
                                            variant=""
                                            htmlType="button"
                                            className={`flex justify-center items-center w-full h-13 rounded-2xl px-4 py-2 border bg-gray-50 text-gray-700 focus:outline-none focus:border-SorrellBrown4000 ${errors[elementId] ? 'border-FailRed400' : 'border-Gray300'
                                                }`}
                                            onClick={() => triggerDownloadUrl(effectiveValue)}
                                            disabled={downloading}
                                        >
                                            {downloading ? <Preloader /> : <UploadSorrell />}
                                            <span className="ms-2 text-SorrellBrown4000 leading-5">
                                                {downloading ? t('form.fileUpload.downloading') : t('form.fileUpload.downloadFile')}
                                            </span>
                                        </Button>
                                    )}

                                    {hasValue && isBase64 && (
                                        <Button
                                            variant=""
                                            htmlType="button"
                                            className={`flex justify-center items-center w-full h-13 rounded-2xl px-4 py-2 border bg-gray-50 text-gray-700 focus:outline-none focus:border-SorrellBrown4000 ${errors[elementId] ? 'border-FailRed400' : 'border-Gray300'
                                                }`}
                                            onClick={() => triggerDownloadDataUrl(effectiveValue)}
                                            disabled={downloading}
                                        >
                                            {downloading ? <Preloader /> : <UploadSorrell />}
                                            <span className="ms-2 text-SorrellBrown4000 leading-5">
                                                {downloading ? t('form.fileUpload.preparing') : t('form.fileUpload.downloadPreview')}
                                            </span>
                                        </Button>
                                    )}
                                </div>

                                {loading ? (
                                    <Preloader variant="sorrell" />
                                ) : (
                                    hasValue && (
                                        <div className="flex items-center mt-2">
                                            <span className="text-gray-700 truncate max-w-[70%]">
                                                {uploadedFileName || guessFileName(effectiveValue, t)}
                                            </span>
                                            <Button
                                                htmlType="button"
                                                type="text"
                                                size="small"
                                                aria-label={t('form.fileUpload.removeFile')}
                                                className="ml-2 text-red-500 hover:text-red-700"
                                                onClick={() => handleRemoveFile(field.onChange)}
                                                disabled={loading || downloading}
                                                icon={<CloseSvg />}
                                            />
                                        </div>
                                    )
                                )}

                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                                    ref={hiddenFileInput}
                                    className="hidden"
                                    onChange={(e) => handleFileChange(e.target.files?.[0], field.onChange)}
                                />
                            </div>

                            {errors[elementId] && (
                                <Span className="text-FailRed400 text-xxs">
                                    {errors[elementId].message ? errors[elementId].message : t('form.thisFieldRequired')}
                                </Span>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};

export default FileUploadClientForm;